// Voter matrix — same visual layout as builder/admin, but each slot is a
// click-target that cycles through the voter's vote states:
//
//   empty → yes → maybe → no → empty → …
//
// The voter clicks directly on slot overlays. Empty cells (no slot) are
// inert. The voter's choices are coloured: green (yes), yellow (maybe),
// red (no), neutral grey outline (no vote yet).

import type { Appointment, FinderConfig } from '../types';
import type { VoteAggregateRow } from '../api';
import { enumerateDates, formatDateShort, formatTime, fromISODate } from '../time';
import { holidaysForCountries, holidayFlagsForCountries } from '../holidays';
import { laneCountForDate, slotCount, slotStartMinutes, zoneForMinutes } from '../logic';
import { projectBusyEvents } from '../busy-overlay';
import type { BusyEvent } from '../ics-parser';
import { el } from './dom';

const TIME_COLUMN_WIDTH = '6.5rem';
const MIN_CELL_WIDTH = 'var(--matrix-min-cell, 2rem)';

type Choice = 'yes' | 'maybe' | 'no';
type Responses = Record<string, Choice>;

export type VoterMatrixOptions = {
  config: FinderConfig;
  slots: Appointment[];
  responses: Responses;
  isClosed: boolean;
  /** Optional: events from the voter's own (browser-only) calendar. */
  busyEvents?: BusyEvent[];
  /** Optional: aggregated tally of all voters per slot. Shown as a tiny
   *  "X✓ Y? Z✗" line at the top of each slot when present. */
  aggregate?: VoteAggregateRow[];
  /** Called whenever a click changes a vote. The new responses are mutated in place. */
  onChange: () => void;
};

export function renderVoterMatrix(opts: VoterMatrixOptions): HTMLElement {
  const root = el('section', { class: 'voter-matrix-panel', 'aria-label': 'Termin-Matrix' });

  // Legend explaining the click cycle.
  root.append(renderLegend());

  const scroller = el('div', { class: 'matrix-scroller' });
  root.append(scroller);

  // Voter sieht nur den tatsächlichen Slot-Bereich, nicht die volle Config.
  // Wenn der Admin „8–18 Uhr" konfiguriert, aber alle Slots zwischen 9 und 13
  // sind, kürzt das die Matrix auf 9–13 Uhr (mit 1 Slot Padding). Spart auf
  // Mobile horizontalen Scroll, fokussiert die Aufmerksamkeit.
  const config = trimConfigForVoter(opts.config, opts.slots);
  const cols = slotCount(config);
  const slotDateSet = new Set(opts.slots.map((s) => s.date));
  const allDates = enumerateDates(config.rangeStart, config.rangeEnd, config.allowedWeekdays);
  const dates = allDates.filter((d) => slotDateSet.has(d));
  if (cols <= 0 || dates.length === 0 || opts.slots.length === 0) {
    scroller.append(el('div', { class: 'matrix-empty' }, 'Keine Termine in dieser Umfrage.'));
    return root;
  }

  const grid = el('div', { class: 'matrix-grid' });
  grid.style.gridTemplateColumns = `${TIME_COLUMN_WIDTH} repeat(${cols}, minmax(${MIN_CELL_WIDTH}, 1fr))`;
  scroller.append(grid);

  const laneCounts = dates.map((d) => laneCountForDate(opts.slots, d));
  const rowOffsets: number[] = [];
  let cursor = 2;
  for (let i = 0; i < dates.length; i++) {
    rowOffsets.push(cursor);
    cursor += laneCounts[i];
  }

  const holidayMap = config.holidayCountries.length > 0
    ? holidaysForCountries(config.holidayCountries, config.rangeStart, config.rangeEnd)
    : new Map<string, string>();
  const holidayFlagsMap = config.holidayCountries.length > 0
    ? holidayFlagsForCountries(config.holidayCountries, config.rangeStart, config.rangeEnd)
    : new Map<string, string>();

  // Header (sticky elements span full axis — see matrix.ts comment).
  const corner = el('div', { class: 'matrix-corner' });
  corner.style.gridRow = '1 / -1';
  corner.style.gridColumn = '1 / -1';
  grid.append(corner);
  for (let c = 0; c < cols; c++) {
    const minutes = slotStartMinutes(config, c);
    const showLabel = config.slotSizeMinutes >= 30 || minutes % 60 === 0;
    const header = el(
      'div',
      { class: `matrix-col-header zone-${zoneForMinutes(minutes)}` },
      showLabel ? formatTime(minutes) : '',
    );
    header.style.gridRow = '1 / -1';
    header.style.gridColumn = String(c + 2);
    grid.append(header);
  }

  // Day rows: row labels + empty visual cells (no clicks; the slot overlays
  // own all interaction).
  dates.forEach((date, dateIdx) => {
    const weekday = fromISODate(date).getDay();
    const isWeekend = weekday === 0 || weekday === 6;
    const holidayName = holidayMap.get(date);
    const laneCount = laneCounts[dateIdx];
    const dayStartRow = rowOffsets[dateIdx];

    const rowLabelWrap = el('div', { class: 'matrix-row-label-wrap' });
    rowLabelWrap.style.gridRow = `${dayStartRow} / ${dayStartRow + laneCount}`;
    rowLabelWrap.style.gridColumn = '1 / -1';

    const rowLabel = el('div', { class: 'matrix-row-label' });
    rowLabel.append(el('span', { class: 'row-date' }, formatDateShort(date)));
    const holidayFlags = holidayFlagsMap.get(date);
    if (holidayFlags) {
      const flagSpan = el('span', { class: 'row-holiday' });
      flagSpan.append(el('span', { class: 'row-holiday-flag' }, holidayFlags));
      if (holidayName) {
        flagSpan.title = holidayName;
        flagSpan.append(el('span', { class: 'row-holiday-name' }, holidayName));
      }
      rowLabel.append(flagSpan);
    }
    if (isWeekend) rowLabel.classList.add('is-weekend');
    if (holidayName) rowLabel.classList.add('is-holiday');
    if (dateIdx > 0) rowLabel.classList.add('is-day-divider');
    rowLabelWrap.append(rowLabel);
    grid.append(rowLabelWrap);

    for (let lane = 0; lane < laneCount; lane++) {
      for (let c = 0; c < cols; c++) {
        const cell = el('div', { class: 'matrix-cell admin-readonly-cell' });
        cell.style.gridRow = String(dayStartRow + lane);
        cell.style.gridColumn = String(c + 2);
        if (isWeekend) cell.classList.add('is-weekend');
        if (holidayName) cell.classList.add('is-holiday');
        if (lane > 0) cell.classList.add('is-sublane');
        if (lane === 0 && dateIdx > 0) cell.classList.add('is-day-top');
        grid.append(cell);
      }
    }
  });

  // Voter's own calendar busy overlays (private, never sent anywhere).
  if (opts.busyEvents && opts.busyEvents.length > 0) {
    const ranges = projectBusyEvents(opts.busyEvents, config, dates);
    for (const r of ranges) {
      const dateIdx = dates.indexOf(r.date);
      if (dateIdx < 0) continue;
      const overlay = el('div', { class: 'busy-overlay' });
      overlay.style.gridRow = `${rowOffsets[dateIdx]} / ${rowOffsets[dateIdx] + laneCounts[dateIdx]}`;
      overlay.style.gridColumn = `${r.startCol + 2} / ${r.endCol + 2}`;
      const summaryText = r.summary || 'belegt';
      overlay.title = r.isRecurring ? `${summaryText} (wiederkehrend)` : summaryText;
      overlay.append(el('span', { class: 'busy-overlay-text' }, summaryText));
      grid.append(overlay);
    }
  }

  // Build aggregate lookup once.
  const aggMap = new Map<string, VoteAggregateRow>();
  if (opts.aggregate) {
    for (const a of opts.aggregate) aggMap.set(a.slot_id, a);
  }

  // Slot overlays: clickable, coloured by current vote.
  for (const slot of opts.slots) {
    const dateIdx = dates.indexOf(slot.date);
    if (dateIdx < 0) continue;
    const absRow = rowOffsets[dateIdx] + slot.lane;
    const overlay = renderSlotOverlay(config, slot, absRow, opts, aggMap.get(slot.id));
    if (overlay) grid.append(overlay);
  }

  return root;
}

function renderSlotOverlay(
  config: FinderConfig,
  slot: Appointment,
  gridRow: number,
  opts: VoterMatrixOptions,
  aggregate?: VoteAggregateRow,
): HTMLElement | null {
  const startCol = (slot.startMinutes - config.dayStartMinutes) / config.slotSizeMinutes;
  const spanCols = (slot.endMinutes - slot.startMinutes) / config.slotSizeMinutes;
  if (!Number.isFinite(startCol) || !Number.isFinite(spanCols) || spanCols <= 0) return null;

  const overlay = el('button', {
    type: 'button',
    class: 'voter-slot-overlay',
    'data-slot-id': slot.id,
    'aria-label': `${formatTime(slot.startMinutes)} bis ${formatTime(slot.endMinutes)}`,
  }) as HTMLButtonElement;
  overlay.style.gridRow = String(gridRow);
  overlay.style.gridColumn = `${Math.round(startCol) + 2} / ${Math.round(startCol) + 2 + Math.round(spanCols)}`;
  if (opts.isClosed) overlay.disabled = true;

  applyChoiceVisual(overlay, opts.responses[slot.id], aggregate);

  overlay.addEventListener('click', () => {
    if (opts.isClosed) return;
    const current = opts.responses[slot.id];
    const next = nextChoice(current);
    if (next === undefined) {
      delete opts.responses[slot.id];
    } else {
      opts.responses[slot.id] = next;
    }
    applyChoiceVisual(overlay, opts.responses[slot.id], aggregate);
    // Mobile-Card mit derselben slot-id parallel updaten.
    const card = document.querySelector(
      `.voter-card[data-slot-id="${slot.id}"]`,
    ) as HTMLElement | null;
    if (card) paintCard(card, opts.responses[slot.id]);
    opts.onChange();
  });

  return overlay;
}

function applyChoiceVisual(
  overlay: HTMLButtonElement,
  choice: Choice | undefined,
  aggregate?: VoteAggregateRow,
): void {
  overlay.classList.remove('is-empty', 'is-yes', 'is-maybe', 'is-no');
  const labelText = `${overlayLabel(choice)} · ${overlay.getAttribute('aria-label') ?? ''}`;
  overlay.title = labelText;

  const children: HTMLElement[] = [];
  if (aggregate && (aggregate.yes + aggregate.maybe + aggregate.no) > 0) {
    children.push(renderAggregateLine(aggregate));
  }

  let symbol: string;
  switch (choice) {
    case 'yes':   overlay.classList.add('is-yes');   symbol = '✓'; break;
    case 'maybe': overlay.classList.add('is-maybe'); symbol = '?'; break;
    case 'no':    overlay.classList.add('is-no');    symbol = '✗'; break;
    default:      overlay.classList.add('is-empty'); symbol = '·';
  }
  children.push(el('span', { class: 'voter-slot-symbol' }, symbol));

  overlay.replaceChildren(...children);
}

function renderAggregateLine(agg: VoteAggregateRow): HTMLElement {
  const wrap = el('span', { class: 'voter-slot-aggregate' });
  if (agg.yes > 0)   wrap.append(el('span', { class: 'agg-yes' },   `${agg.yes}✓`));
  if (agg.maybe > 0) wrap.append(el('span', { class: 'agg-maybe' }, `${agg.maybe}?`));
  if (agg.no > 0)    wrap.append(el('span', { class: 'agg-no' },    `${agg.no}✗`));
  return wrap;
}

function overlayLabel(choice: Choice | undefined): string {
  switch (choice) {
    case 'yes':   return 'Kann';
    case 'maybe': return 'Vielleicht';
    case 'no':    return 'Nein';
    default:      return 'noch keine Antwort';
  }
}

/** Mobile-Layout: senkrechte Liste von Slot-Karten mit drei Knöpfen (✓ ? ✗).
 *  Wird via CSS @media nur auf schmalen Viewports gezeigt. Bindet auf
 *  denselben `responses`-Object und ruft denselben onChange wie die Matrix
 *  — beide Views bleiben automatisch synchron. */
export function renderVoterCards(opts: VoterMatrixOptions): HTMLElement {
  const root = el('section', { class: 'voter-cards-panel', 'aria-label': 'Termin-Liste (Mobil)' });
  const sorted = [...opts.slots].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    return a.startMinutes - b.startMinutes;
  });
  if (sorted.length === 0) {
    root.append(el('p', { class: 'voter-cards-empty' }, 'Keine Termine in dieser Umfrage.'));
    return root;
  }
  const aggMap = new Map<string, VoteAggregateRow>();
  for (const a of opts.aggregate ?? []) aggMap.set(a.slot_id, a);

  // Tagesweise gruppieren — kompakter als „Slot-pro-Karte" mit Datum drauf.
  let currentDate = '';
  for (const slot of sorted) {
    if (slot.date !== currentDate) {
      currentDate = slot.date;
      root.append(el('h3', { class: 'voter-cards-day' }, formatDateShort(slot.date)));
    }
    root.append(renderCard(slot, opts, aggMap.get(slot.id)));
  }
  return root;
}

function renderCard(
  slot: Appointment,
  opts: VoterMatrixOptions,
  agg: VoteAggregateRow | undefined,
): HTMLElement {
  const card = el('div', { class: 'voter-card', 'data-slot-id': slot.id });
  const meta = el('div', { class: 'voter-card-meta' });
  meta.append(
    el('span', { class: 'voter-card-time' },
      `${formatTime(slot.startMinutes)} – ${formatTime(slot.endMinutes)}`),
  );
  if (agg && (agg.yes + agg.maybe + agg.no) > 0) {
    const aggLine = el('span', { class: 'voter-card-aggregate' });
    if (agg.yes > 0)   aggLine.append(el('span', { class: 'agg-yes' },   `${agg.yes}✓`));
    if (agg.maybe > 0) aggLine.append(el('span', { class: 'agg-maybe' }, `${agg.maybe}?`));
    if (agg.no > 0)    aggLine.append(el('span', { class: 'agg-no' },    `${agg.no}✗`));
    meta.append(aggLine);
  }
  card.append(meta);

  // Drei Knöpfe statt zyklischem Klick — auf Mobil eindeutiger.
  const choicesRow = el('div', { class: 'voter-card-choices' });
  for (const c of ['yes', 'maybe', 'no'] as Choice[]) {
    const btn = el('button', {
      type: 'button',
      class: `voter-card-choice voter-card-${c}`,
      'aria-label': overlayLabel(c),
    }, overlayLabelSymbol(c)) as HTMLButtonElement;
    if (opts.isClosed) btn.disabled = true;
    btn.addEventListener('click', () => {
      if (opts.isClosed) return;
      // Wenn schon dieser Choice aktiv → wieder leer. Sonst auf c setzen.
      if (opts.responses[slot.id] === c) {
        delete opts.responses[slot.id];
      } else {
        opts.responses[slot.id] = c;
      }
      paintCard(card, opts.responses[slot.id]);
      // Desktop-Matrix-Overlay parallel updaten (gleicher data-slot-id).
      // Kein .click() — das würde nochmal toggeln. Wir setzen die Klassen
      // direkt und überlassen den Aggregate-Re-Render dem onChange-Aufruf.
      const overlay = document.querySelector(
        `.voter-slot-overlay[data-slot-id="${slot.id}"]`,
      ) as HTMLButtonElement | null;
      if (overlay) {
        overlay.classList.remove('is-empty', 'is-yes', 'is-maybe', 'is-no');
        const next = opts.responses[slot.id];
        overlay.classList.add(next ? `is-${next}` : 'is-empty');
        const sym = overlay.querySelector('.voter-slot-symbol');
        if (sym) {
          sym.textContent = next === 'yes' ? '✓' : next === 'maybe' ? '?' : next === 'no' ? '✗' : '·';
        }
      }
      opts.onChange();
    });
    choicesRow.append(btn);
  }
  card.append(choicesRow);
  paintCard(card, opts.responses[slot.id]);
  return card;
}

function overlayLabelSymbol(c: Choice): string {
  return c === 'yes' ? '✓ Kann' : c === 'maybe' ? '? Vielleicht' : '✗ Nein';
}

function paintCard(card: HTMLElement, choice: Choice | undefined): void {
  card.classList.remove('is-yes', 'is-maybe', 'is-no');
  if (choice) card.classList.add(`is-${choice}`);
  const buttons = card.querySelectorAll('.voter-card-choice');
  buttons.forEach((b) => {
    b.classList.remove('is-active');
    const isActive =
      (choice === 'yes' && b.classList.contains('voter-card-yes')) ||
      (choice === 'maybe' && b.classList.contains('voter-card-maybe')) ||
      (choice === 'no' && b.classList.contains('voter-card-no'));
    if (isActive) b.classList.add('is-active');
  });
}

/** Schneidet die Builder-Config auf den tatsächlichen Slot-Bereich zu, plus
 *  1 Slot Padding auf jeder Seite (clamp auf die Original-Range). Wenn der
 *  Admin „8–18 Uhr" konfiguriert, aber alle Slots zwischen 9 und 13 sind,
 *  liefert das eine Config mit dayStart=8:30/9:00 und dayEnd=13:30/14:00.
 *  Slot-Größe und Wochentage bleiben unverändert. */
function trimConfigForVoter(config: FinderConfig, slots: Appointment[]): FinderConfig {
  if (slots.length === 0) return config;
  const minStart = Math.min(...slots.map((s) => s.startMinutes));
  const maxEnd   = Math.max(...slots.map((s) => s.endMinutes));
  const size = config.slotSizeMinutes;
  // Snap auf Slot-Grid + 1 Slot Padding, dann clamp auf Original-Range.
  const start = Math.max(config.dayStartMinutes, Math.floor(minStart / size) * size - size);
  const end   = Math.min(config.dayEndMinutes,   Math.ceil(maxEnd / size) * size + size);
  if (start >= end) return config;
  return { ...config, dayStartMinutes: start, dayEndMinutes: end };
}

function nextChoice(current: Choice | undefined): Choice | undefined {
  switch (current) {
    case undefined: return 'yes';
    case 'yes':     return 'maybe';
    case 'maybe':   return 'no';
    case 'no':      return undefined;
  }
}

function renderLegend(): HTMLElement {
  const legend = el('div', { class: 'voter-legend' });
  legend.append(
    el('span', { class: 'voter-legend-label' }, 'Klicke auf einen Termin:'),
    legendStep('·', 'nein (Default)', 'voter-legend-empty'),
    el('span', { class: 'voter-legend-arrow' }, '→'),
    legendStep('✓', 'kann', 'voter-legend-yes'),
    el('span', { class: 'voter-legend-arrow' }, '→'),
    legendStep('?', 'vielleicht', 'voter-legend-maybe'),
    el('span', { class: 'voter-legend-arrow' }, '→'),
    legendStep('✗', 'nein', 'voter-legend-no'),
    el('span', { class: 'voter-legend-arrow' }, '→ Default'),
  );
  return legend;
}

function legendStep(symbol: string, label: string, cls: string): HTMLElement {
  return el(
    'span',
    { class: `voter-legend-step ${cls}` },
    el('span', { class: 'voter-legend-symbol' }, symbol),
    ' ',
    el('span', { class: 'voter-legend-text' }, label),
  );
}
